import { useEffect, useRef, useState } from "react";
import {
  getDropiIntegrations,
  getIntegrationsAvailability,
} from "../../../services/integrations/integrationsService";
import {
  getAllPaymentMethods,
  getPaymentMethodConfiguration,
} from "../../../services/payment/payment";

import {
  IntegrationType,
  INTEGRATIONS_UI_CONFIG,
} from "../config/integrations.config";
import { IntegrationAvailabilityApi } from "../config/integrations.types";
import { resolveAvailableIntegrations } from "../helpers/resolveAvailableIntegrations";
import { PaymentMethodStatus } from "../../payment/PaymentMethods/PaymentMethodsInterface";
import { DropiIntegrationInterface } from "../../../services/integrations/integrationsService";
import { Country } from "../../../const/countryList";

export const useIntegrations = (country: Country = Country.COLOMBIA) => {
  const [integrationsDropi, setIntegrationsDropi] =
    useState<DropiIntegrationInterface>();
  const methodsStatus = useRef<PaymentMethodStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState<any[]>([]);

  const resolveIntegrationState = (
    type: IntegrationType,
    dropiData?: DropiIntegrationInterface
  ) => {
    if (type === "dropi") {
      const dropiIntegration = dropiData ?? integrationsDropi;
      const isActive = dropiIntegration?.is_active ?? false;
      return {
        isActive,
        isConnected: isActive,
        alreadyConfigured: isActive,
      };
    }

    const method = methodsStatus.current.find((m) => m.id === type);
    const active = method?.active ?? false;
    const alreadyConfigured = method?.already_configured ?? false;
    
    return {
      isActive: active && alreadyConfigured,
      isConnected: active && alreadyConfigured,
      alreadyConfigured,
    };
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      try {
        const results = await Promise.allSettled([
          getIntegrationsAvailability(),
          getDropiIntegrations(),
          getAllPaymentMethods(),
          getPaymentMethodConfiguration(),
        ]);

        const availabilityApi = results[0].status === 'fulfilled' 
          ? results[0].value 
          : null;
        
        const dropiIntegration = results[1].status === 'fulfilled' 
          ? results[1].value 
          : undefined;
        
        const paymentConfig = results[3].status === 'fulfilled' 
          ? results[3].value 
          : [];

        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const endpointNames = [
              'getIntegrationsAvailability',
              'getDropiIntegrations',
              'getAllPaymentMethods',
              'getPaymentMethodConfiguration'
            ] as const;
            const error = result.reason;
            const is404 = error?.response?.status === 404;
            
            if (is404) {
              console.warn(`${endpointNames[index]} returned 404 (expected for some users)`);
            } else {
              console.error(`Error in ${endpointNames[index]}:`, error);
            }
          }
        });

        if (dropiIntegration) {
          setIntegrationsDropi(dropiIntegration);
        }
        
        methodsStatus.current = paymentConfig;

        if (availabilityApi) {
          const available = resolveAvailableIntegrations({
            apiIntegrations: availabilityApi as IntegrationAvailabilityApi[],
            country,
          });

          const finalIntegrations = available.map((integration) => {
            const ui = INTEGRATIONS_UI_CONFIG[integration.id];
            const state = resolveIntegrationState(integration.id, dropiIntegration);

            return {
              ...ui,
              ...state,
              type: integration.id,
            };
          });

          setIntegrations(finalIntegrations);
        } else {
          setIntegrations([]);
        }
      } catch (error) {
        console.error("Unexpected error loading integrations:", error);
        setIntegrations([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [country]);

  return {
    integrations,
    loading,
  };
};
